import { useState, useRef } from "react";
import { Button, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, Grid, TextField,
    FormControl, FormControlLabel, Select, MenuItem,
    InputLabel, Checkbox, InputAdornment, Alert,
    AlertTitle, Paper, FormHelperText, Box, Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Draggable from 'react-draggable';
import dayjs from 'dayjs';
/* ICONS */
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { nameValidation, normalizeString } from 'utils/Validations'
import { TimekeeperService } from "services/TimekeeperService";

const PaperComponent = (props) => {
    const nodeRef = useRef(null);
    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const AddOvertime = ({ open, setOpen, reloading, rows }) => {
    const tk = new TimekeeperService();
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [validity_period, setValidityPeriod] = useState('day')
    const [restart_date, setRestartDate] = useState(dayjs())
    const [restart_week, setRestartWeek] = useState('monday')
    const [required_approval, setRequiredApproval] = useState(false)
    const [loadingOvertime, setLoadingOvertime] = useState(false)
    const [alert, setAlert] = useState(false)
    const [showError, setShowError] = useState({})
    const [timeInputs, setTimeInputs] = useState({
        overtime_start: "00:00",
        min_hour_calification: "00:10"
    });


    const closeForm = () => {
        setOpen(false)
        setName('')
        setDescription('')
        setTimeInputs({
            overtime_start: "00:00",
            min_hour_calification: "00:10"
        });
        setValidityPeriod('day')
        setRestartDate(dayjs())
        setRestartWeek('monday')
        setRequiredApproval(false)
        setLoadingOvertime(false)
        setAlert(false)
        setShowError({})
    }

    const existName = (name) => {
        const normalizedInput = normalizeString(name);
        return rows.some((row) => normalizeString(row.name) === normalizedInput);
    };

    const disabled = () => {
        return name === '' || existName(name) || showError.overtime_start === true || showError.min_hour_calification === true || (validity_period === 'fortnight' && restart_date === '') || (validity_period === 'week' && restart_week === '')
    }

    const changes = () => {
        const hasChanges = name !== '' || description !== '' || timeInputs.overtime_start !== '00:00' || timeInputs.min_hour_calification !== '00:10' || validity_period !== '' || restart_date !== null || restart_week !== null || required_approval !== false
        return hasChanges ? setAlert(true) : closeForm()
    }

    const AddOvertimes = async () => {
        setLoadingOvertime(true)
        try {
            const new_overtime = {
                name: name,
                description: description,
                overtime_start: timeInputs.overtime_start,
                min_hour_calification: timeInputs.min_hour_calification,
                validity_period: validity_period,
                restart_date: dayjs(restart_date),
                restart_week: restart_week,
                required_approval: required_approval
            }
            console.log('new_overtime: ', new_overtime)
            const response = await tk.overtime.createOvertime(new_overtime)
            console.log('response: ', response)
            enqueueSnackbar(t('settings.overtimes.alerts.add.success'), { variant: 'success' })
            reloading()
            closeForm()


        } catch (error) {
            console.error(error)
            enqueueSnackbar(t('settings.overtimes.alerts.add.error'), { variant: 'error' })
            setLoadingOvertime(false)
        }
    }


    // Función para formatear el tiempo mientras el usuario escribe
    const formatTimeInput = (value) => {
        // Remover todo lo que no sea número
        const numbersOnly = value.replace(/\D/g, '');

        // Limitar a máximo 5 dígitos
        const truncated = numbersOnly.slice(0, 5);

        // Formatear según la longitud
        if (truncated.length <= 2) {
            return truncated;
        } else if (truncated.length === 3) {
            return truncated;
        } else if (truncated.length === 4) {
            // Formato 00:00
            return `${truncated.slice(0, 2)}:${truncated.slice(2)}`;
        } else if (truncated.length === 5) {
            // Formato 000:00
            return `${truncated.slice(0, 3)}:${truncated.slice(3)}`;
        }

        return truncated;
    };

    const validateTimeInput = (value) => {
        const errors = {
            hasError: false,
            message: ''
        };

        // Si hay menos de 4 caracteres (sin contar los dos puntos)
        const numbersOnly = value.replace(/\D/g, '');
        if (numbersOnly.length < 4) {
            errors.hasError = true;
            errors.message = 'Mínimo 4 dígitos requeridos (formato 00:00)';
            return errors;
        }

        // Validar formato con regex
        const regex4Digits = /^([0-9]{2}):([0-5][0-9])$/; // 00:00
        const regex5Digits = /^([0-9]{3}):([0-5][0-9])$/; // 000:00

        if (!regex4Digits.test(value) && !regex5Digits.test(value)) {
            errors.hasError = true;
            errors.message = 'Formato inválido. Use 00:00 o 000:00 con minutos válidos (00-59)';
            return errors;
        }

        // Extraer minutos y validar que no excedan 59
        const parts = value.split(':');
        if (parts.length === 2) {
            const minutes = parseInt(parts[1], 10);
            if (minutes > 59) {
                errors.hasError = true;
                errors.message = 'Los minutos no pueden ser mayores a 59';
                return errors;
            }
        }

        return errors;
    };

    const handleChange = (fieldName, value) => {
        const formattedValue = formatTimeInput(value);
        setTimeInputs(prev => ({ ...prev, [fieldName]: formattedValue }));

        // Validar inmediatamente
        const validation = validateTimeInput(formattedValue);
        setShowError(prev => ({
            ...prev,
            [fieldName]: validation.hasError,
            error: validation.message
        }));
    };


    return (
        <Dialog
            maxWidth="sm"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
            autoFocus
            scroll='paper'
        >
            <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>{t('settings.overtimes.titles.add')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alert && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlert(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            id='input-name'
                            name='name'
                            autoComplete="off"
                            autoFocus
                            fullWidth
                            label={t('settings.overtimes.dialog.fields.name')}
                            value={name}
                            onChange={(e) => {
                                if (nameValidation(e.target.value)) {
                                    setName(e.target.value);
                                    existName(e.target.value)
                                }
                            }}
                            error={existName(name)}
                            slotProps={{
                                htmlInput: { maxLength: 100 },
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${name.length}/100`}</InputAdornment>
                                }
                            }}
                            required
                        />
                        <FormHelperText
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 0.5,
                                px: 1.75
                            }}
                        >
                            <span>{existName(name) ? t('settings.overtimes.dialog.fields.exist_name') : ''}</span>
                        </FormHelperText>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                        <TextField
                            id='input-description'
                            name='description'
                            fullWidth
                            autoComplete="off"
                            autoFocus
                            label={t('settings.overtimes.dialog.fields.description')}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            slotProps={{
                                htmlInput: { maxLength: 255 },
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${description.length}/255`}</InputAdornment>
                                }
                            }}
                            rows={2}
                            multiline
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ mt: 1.5 }}>
                        <TextField
                            id='input-overtime_start'
                            name='overtime_start'
                            autoComplete="off"
                            autoFocus
                            fullWidth
                            label={t('settings.overtimes.dialog.fields.overtime_start_after')}
                            value={timeInputs.overtime_start}
                            onChange={(e) => handleChange('overtime_start', e.target.value)}
                            required
                            slotProps={{
                                input: {
                                    endAdornment: showError.overtime_start && (
                                        <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                            <Tooltip title={`${showError.error}`}>
                                                <InfoIcon color="error" />
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }
                            }}
                            error={showError.overtime_start}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ mt: 1.5 }}>
                        <TextField
                            id={'input-min_hour_calification'}
                            name='min_hour_calification'
                            autoComplete="off"
                            autoFocus
                            fullWidth
                            label={t('settings.overtimes.dialog.fields.min_hour_calification')}
                            value={timeInputs.min_hour_calification}
                            onChange={(e) => handleChange('min_hour_calification', e.target.value)}
                            required
                            slotProps={{
                                input: {
                                    endAdornment: showError.min_hour_calification && (
                                        <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                            <Tooltip title={'Formato: 00:00 o 000:00, minutos máximo 59'}>
                                                <InfoIcon color="error" />
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }
                            }}
                            error={showError.min_hour_calification}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ mt: 1.5 }}>
                        <FormControl fullWidth>
                            <InputLabel id="validity_period-select-label">{t('settings.overtimes.dialog.fields.validity_period') + " * "}</InputLabel>
                            <Select
                                id="validity_period-select"
                                name="validity_period"
                                fullWidth
                                labelId="validity_period-select-label"
                                label={t('settings.overtimes.dialog.fields.validity_period')}
                                value={validity_period}
                                onChange={(e) => setValidityPeriod(e.target.value)}
                            >
                                <MenuItem key="day" value="day">{t('settings.overtimes.dialog.fields.validity_period_data.day')}</MenuItem>
                                <MenuItem key="week" value="week">{t('settings.overtimes.dialog.fields.validity_period_data.week')}</MenuItem>
                                <MenuItem key="fortnight" value="fortnight">{t('settings.overtimes.dialog.fields.validity_period_data.fortnight')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ mt: 1.5 }}>
                        {validity_period === "fortnight" &&
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                                <DatePicker
                                    label={t('settings.overtimes.dialog.fields.restart_date')}
                                    name="restart_date"
                                    onChange={(e) => setRestartDate(e)}
                                    value={dayjs(restart_date)}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            fullWidth: true,
                                        },

                                    }}
                                />
                            </LocalizationProvider>
                        }
                        {validity_period === "week" &&
                            <FormControl fullWidth>
                                <InputLabel id="restart_week-select-label">{t('settings.overtimes.dialog.fields.restart_week') + " * "}</InputLabel>
                                <Select
                                    id="restart_week-select"
                                    name="restart_week"
                                    required
                                    labelId="restart_week-select-label"
                                    label={t('settings.overtimes.dialog.fields.restart_week')}
                                    value={restart_week}
                                    onChange={(e) => setRestartWeek(e.target.value)}

                                >
                                    <MenuItem key="monday" value="monday" >{t('settings.overtimes.dialog.fields.restart_week_data.monday')}</MenuItem>
                                    <MenuItem key="tuesday" value="tuesday">{t('settings.overtimes.dialog.fields.restart_week_data.tuesday')}</MenuItem>
                                    <MenuItem key="wednesday" value="wednesday">{t('settings.overtimes.dialog.fields.restart_week_data.wednesday')}</MenuItem>
                                    <MenuItem key="thursday" value="thursday">{t('settings.overtimes.dialog.fields.restart_week_data.thursday')}</MenuItem>
                                    <MenuItem key="friday" value="friday">{t('settings.overtimes.dialog.fields.restart_week_data.friday')}</MenuItem>
                                    <MenuItem key="saturday" value="saturday">{t('settings.overtimes.dialog.fields.restart_week_data.saturday')}</MenuItem>
                                    <MenuItem key="sunday" value="sunday">{t('settings.overtimes.dialog.fields.restart_week_data.sunday')}</MenuItem>
                                </Select>
                            </FormControl>
                        }
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <FormControlLabel
                            control={<Checkbox
                                name="required_approval"
                                checked={required_approval}
                                onChange={(e) => setRequiredApproval(e.target.checked)}
                            />}
                            label={t('settings.overtimes.dialog.fields.required_approval')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => changes()} color="error" variant="contained" >{t('general.buttons.cancel')}</Button>
                <Button loading={loadingOvertime} disabled={disabled()} onClick={() => AddOvertimes()} color="primary" variant="contained"> {t('general.buttons.save')}</Button>
            </DialogActions>
        </Dialog>
    )
}

export { AddOvertime };